import { useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Appeal, AppealSourceModule } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const SOURCE_LABEL: Record<AppealSourceModule, string> = {
  s02: 'Statutory benefit decision',
  s03: 'Welfare assistance decision',
};

/** S08 now exposes GET /api/v1/cases/appeals/me — real server-side history. */
export function CitizenAppeals() {
  const { persona } = useAuth();
  const appealsQ = useApiQuery<{ items: Appeal[] }>('/api/v1/cases/appeals/me');
  const appeals = appealsQ.data?.items ?? [];
  const [sourceModule, setSourceModule] = useState<AppealSourceModule>('s02');
  const [sourceDecisionRef, setSourceDecisionRef] = useState('');
  const [grounds, setGrounds] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!persona?.nin) return;
    setBusy(true);
    setError(null);
    try {
      await api.post<Appeal>('/api/v1/cases/appeals', {
        nin: persona.nin,
        sourceModule,
        sourceDecisionRef,
        grounds,
        lodgedAt: new Date().toISOString().slice(0, 10),
      });
      appealsQ.reload();
      setSourceDecisionRef('');
      setGrounds('');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not lodge your appeal');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="Appeals" subtitle="Disagree with a benefit or welfare decision? Lodge an appeal to the Social Protection Appeals Tribunal." />

      <div className="grid gap-6 lg:grid-cols-[1fr_1.3fr]">
        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Lodge a new appeal</h2>
          {error ? (
            <div className="mb-3">
              <ErrorNote message={error} />
            </div>
          ) : null}
          <div className="space-y-3">
            <Field label="What decision are you appealing?">
              <select className="field-input" value={sourceModule} onChange={(e) => setSourceModule(e.target.value as AppealSourceModule)}>
                <option value="s02">Statutory benefit decision</option>
                <option value="s03">Welfare assistance decision</option>
              </select>
            </Field>
            <Field label="Claim / application reference">
              <input
                className="field-input"
                value={sourceDecisionRef}
                onChange={(e) => setSourceDecisionRef(e.target.value)}
                placeholder="e.g. BCL-2026-0004821 or WFA-2026-0001193"
              />
            </Field>
            <Field label="Grounds for appeal">
              <textarea
                className="field-input"
                rows={5}
                value={grounds}
                onChange={(e) => setGrounds(e.target.value)}
                placeholder="Explain why you believe the decision was wrong…"
              />
            </Field>
            <button className="btn-primary w-full" onClick={submit} disabled={busy || sourceDecisionRef.length < 1 || grounds.length < 10}>
              {busy ? 'Submitting…' : 'Lodge appeal'}
            </button>
            <p className="text-xs text-slate-400">
              The Tribunal must acknowledge your appeal within 7 days and decide within 60 days of lodging.
            </p>
          </div>
        </div>

        <div>
          <h2 className="mb-3 text-sm font-bold text-slate-700">Your appeals</h2>
          <div className="card overflow-x-auto">
            {appealsQ.loading ? (
              <Spinner label="Loading your appeals…" />
            ) : appealsQ.error ? (
              <ErrorNote message={appealsQ.error} />
            ) : appeals.length === 0 ? (
              <EmptyState title="No appeals lodged" hint="Appeals you lodge will appear here." />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Reference</th>
                    <th className="table-head">Against</th>
                    <th className="table-head">Status</th>
                    <th className="table-head">Ack. due</th>
                    <th className="table-head">Decision due</th>
                  </tr>
                </thead>
                <tbody>
                  {appeals.map((a) => (
                    <tr key={a.id}>
                      <td className="table-cell font-medium">{a.refNumber}</td>
                      <td className="table-cell text-xs">
                        {SOURCE_LABEL[a.sourceModule]}
                        <div className="text-slate-400">{a.sourceDecisionRef}</div>
                      </td>
                      <td className="table-cell">
                        <StatusBadge status={a.status} />
                        {a.outcome ? (
                          <div className="mt-1">
                            <StatusBadge status={a.outcome} />
                          </div>
                        ) : null}
                      </td>
                      <td className="table-cell text-xs text-slate-500">{formatDate(a.ackDueAt)}</td>
                      <td className="table-cell text-xs text-slate-500">{formatDate(a.decisionDueAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
